import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  emendasApi,
  type AcaoEmenda,
  type Emenda,
  type ExecucaoDireta,
  type StatusExecucao,
} from '@/lib/api/emendas';
import { useAuthStore } from '@/lib/stores/authStore';
import { useBairros } from '@/lib/hooks/useBairros';
import { useEscala } from '@/lib/escala';
import { toast } from '@/lib/stores/toastStore';
import { Drawer } from '@/design-system/Drawer';
import { Info } from '@/design-system/Info';

const brl = (v: number) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const mil = (v: number) => `R$ ${((v || 0) / 1000).toFixed(0)} mil`;

const ROTULO_REGIME: Record<string, string> = {
  map: 'MAP derivado',
  pendente: 'decisão pendente',
  direto: 'direto',
};

const EXECUCAO_VAZIA: ExecucaoDireta = { indicado: 0, empenhado: 0, pago: 0 };

export function EmendasPage() {
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();
  const { bairros } = useBairros();
  // Rotulo da unidade territorial segue a escala do mandato (bairro, municipio, estado...)
  const { unidade } = useEscala();

  const exercicio = new Date().getFullYear();

  const { data: emendas = [], isLoading, isError, error } = useQuery({
    queryKey: ['emendas'],
    queryFn: () => emendasApi.list(token),
    enabled: !!token,
  });

  const [searchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [modalNova, setModalNova] = useState(false);

  // Vem do drawer de ação (AcoesPage): ?emenda=<id> pré-seleciona a emenda
  // vinculada, para o link "com a emenda selecionada" funcionar de fato.
  useEffect(() => {
    const alvo = searchParams.get('emenda');
    if (alvo && emendas.some((e) => e.id === alvo)) setSelectedId(alvo);
  }, [searchParams, emendas]);

  // Rascunhos editáveis: um PUT por tecla digitada seria inviável, então a
  // edição fica local e sobe num salvamento explícito.
  const [acoesDraft, setAcoesDraft] = useState<AcaoEmenda[]>([]);
  const [diretoDraft, setDiretoDraft] = useState<ExecucaoDireta>(EXECUCAO_VAZIA);

  const selecionada: Emenda | undefined =
    emendas.find((e) => e.id === selectedId) || emendas[0];

  useEffect(() => {
    if (!selecionada) return;
    setAcoesDraft(selecionada.acoes || []);
    setDiretoDraft(selecionada.direto || EXECUCAO_VAZIA);
  }, [selecionada?.id, selecionada?.updated_at]);

  const erroDe = (err: any, padrao: string) => {
    const msg = err?.response?.data?.message;
    return Array.isArray(msg) ? msg.join(' · ') : msg || padrao;
  };

  const invalidar = () => queryClient.invalidateQueries({ queryKey: ['emendas'] });

  const updateMutation = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Partial<Emenda> }) =>
      emendasApi.update(id, dados, token),
    onSuccess: () => {
      invalidar();
      toast('Execução da emenda atualizada.');
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao atualizar a emenda.')),
  });

  const createMutation = useMutation({
    mutationFn: (dados: Partial<Emenda>) => emendasApi.create(dados, token),
    onSuccess: (nova) => {
      invalidar();
      toast(`Emenda ${nova.codigo} indicada.`);
      setSelectedId(nova.id);
      setModalNova(false);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao criar a emenda.')),
  });

  const regimeMutation = useMutation({
    mutationFn: ({ id, decisao }: { id: string; decisao: 'migrar' | 'descartar' }) =>
      emendasApi.decidirRegime(id, decisao, token),
    onSuccess: (e) => {
      invalidar();
      toast(
        e.regime === 'map'
          ? 'Plano de trabalho migrado: a emenda passou a derivar do MAP.'
          : 'MAP descartado: a emenda segue em execução direta.',
      );
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao decidir o regime.')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => emendasApi.delete(id, token),
    onSuccess: () => {
      invalidar();
      toast('Emenda excluída.');
      setSelectedId(null);
    },
    // O backend recusa a exclusão quando há ações custeadas pela emenda
    onError: (err: any) => toast(erroDe(err, 'Erro ao excluir a emenda.')),
  });

  // Totais da carteira, somando o que o servidor já consolidou por emenda
  const totalIndicado = emendas.reduce((a, e) => a + (e.total_indicado || 0), 0);
  const totalEmpenhado = emendas.reduce((a, e) => a + (e.total_empenhado || 0), 0);
  const totalPago = emendas.reduce((a, e) => a + (e.total_pago || 0), 0);
  const percExecucao = totalIndicado > 0 ? Math.round((totalPago / totalIndicado) * 100) : 0;

  const nomeBairro = (bairro?: string) =>
    bairros.find((b) => b.n === bairro)?.n || bairro || 'Sem território';

  const salvarAcoes = () => {
    if (!selecionada) return;
    updateMutation.mutate({ id: selecionada.id, dados: { acoes: acoesDraft } });
  };

  const salvarDireto = () => {
    if (!selecionada) return;
    updateMutation.mutate({ id: selecionada.id, dados: { direto: diretoDraft } });
  };

  const excluir = () => {
    if (!selecionada) return;
    if (window.confirm(`Excluir a emenda ${selecionada.codigo}?`)) {
      deleteMutation.mutate(selecionada.id);
    }
  };

  /** Exporta a carteira carregada como CSV, sem depender de endpoint. */
  const exportarCsv = () => {
    const linhas = [
      'codigo,titulo,bairro,beneficiario,exercicio,regime,indicado,empenhado,pago',
      ...emendas.map((e) =>
        [
          e.codigo,
          `"${e.titulo.replace(/"/g, '""')}"`,
          e.bairro || '',
          `"${(e.beneficiario || '').replace(/"/g, '""')}"`,
          e.exercicio,
          e.regime,
          e.total_indicado,
          e.total_empenhado,
          e.total_pago,
        ].join(','),
      ),
    ];
    const blob = new Blob(['﻿' + linhas.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `emendas-${exercicio}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast(`${emendas.length} emenda(s) exportada(s).`);
  };

  const alterarAcao = (i: number, campo: 'nome' | 'valor' | 'status', valor: any) => {
    setAcoesDraft((prev) => prev.map((a, idx) => (idx === i ? { ...a, [campo]: valor } : a)));
  };

  const totalDraft = acoesDraft.reduce((a, x) => a + (Number(x.valor) || 0), 0);

  return (
    <div className="page" id="p-emendas">
      {/* Cabeçalho */}
      <div className="head">
        <div>
          <h1>Emendas parlamentares</h1>
          <p>
            Indicação, execução e prestação de contas por território. Regra binária: emenda sem MAP tem valores lançados; emenda com MAP tem valores derivados e somente leitura.
          </p>
        </div>
        <div className="act">
          <button className="btn" onClick={exportarCsv} disabled={emendas.length === 0}>
            Exportar
          </button>
          <button className="btn pri" onClick={() => setModalNova(true)}>
            Nova emenda
          </button>
        </div>
      </div>

      {/* 4 KPIs */}
      <div className="grid g4">
        <div className="card kpi">
          <div className="l">Indicado {exercicio}</div>
          <div className="v">{mil(totalIndicado)}</div>
          <div className="d">soma de todas as emendas</div>
        </div>
        <div className="card kpi">
          <div className="l">Empenhado</div>
          <div className="v" style={{ color: 'var(--warn)' }}>{mil(totalEmpenhado)}</div>
          <div className="d">garantido pelo Executivo</div>
        </div>
        <div className="card kpi">
          <div className="l">Pago</div>
          <div className="v" style={{ color: 'var(--good)' }}>{mil(totalPago)}</div>
          <div className="d">recursos liberados na conta</div>
        </div>
        <div className="card kpi">
          <div className="l">Execução Financeira</div>
          <div className="v">{percExecucao}%</div>
          <div className="d">{brl(totalPago)} pagos</div>
        </div>
      </div>

      {isLoading && <div className="empty">Carregando emendas...</div>}
      {isError && !isLoading && (
        <div className="empty">
          Não foi possível carregar as emendas: {erroDe(error, 'erro de comunicação com a API.')}
        </div>
      )}
      {!isLoading && !isError && emendas.length === 0 && (
        <div className="empty">
          Nenhuma emenda indicada ainda. Use <b>Nova emenda</b> para registrar a primeira.
        </div>
      )}

      {selecionada && (
        <div className="grid g21" style={{ marginTop: 12 }}>
          {/* Lado Esquerdo: Lista de Emendas */}
          <div className="card flat tw">
            <table>
              <thead>
                <tr>
                  <th>Código / Objeto</th>
                  <th>{unidade}</th>
                  <th>Regime</th>
                  <th>Indicado</th>
                  <th>Pago</th>
                </tr>
              </thead>
              <tbody>
                {emendas.map((e) => (
                  <tr
                    key={e.id}
                    className="row"
                    style={e.id === selecionada.id ? { background: 'var(--accent-soft)' } : {}}
                    onClick={() => setSelectedId(e.id)}
                  >
                    <td>
                      <b className="mono">{e.codigo}</b>
                      <div style={{ fontSize: 12, color: 'var(--ink2)' }}>{e.titulo}</div>
                    </td>
                    <td>{nomeBairro(e.bairro)}</td>
                    <td>
                      <span
                        className={`chip ${
                          e.regime === 'map' ? 'acc' : e.regime === 'pendente' ? 'warn' : 'neutral'
                        }`}
                      >
                        {ROTULO_REGIME[e.regime]}
                      </span>
                    </td>
                    <td className="num">
                      <b>R$ {((e.total_indicado || 0) / 1000).toFixed(0)}k</b>
                    </td>
                    <td className="num" style={{ color: 'var(--good)' }}>
                      R$ {((e.total_pago || 0) / 1000).toFixed(0)}k
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Lado Direito: Detalhe da emenda selecionada */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h2 style={{ margin: 0 }}>
                {selecionada.codigo} · {selecionada.titulo}
              </h2>
              <span
                className={`chip ${
                  selecionada.regime === 'map'
                    ? 'acc'
                    : selecionada.regime === 'pendente'
                    ? 'warn'
                    : 'neutral'
                }`}
              >
                {selecionada.regime === 'map' ? 'valores derivados do MAP' : ROTULO_REGIME[selecionada.regime]}
              </span>
            </div>

            <dl className="dl" style={{ marginBottom: 14 }}>
              <dt>Território</dt>
              <dd>{nomeBairro(selecionada.bairro)}</dd>
              <dt>Beneficiário</dt>
              <dd>{selecionada.beneficiario || '—'}</dd>
              <dt>Exercício</dt>
              <dd>{selecionada.exercicio}</dd>
            </dl>

            {/* --- Regime MAP: valores derivados do plano de trabalho --- */}
            {selecionada.regime === 'map' && (
              <>
                <h3 style={{ margin: '14px 0 6px', fontSize: 13 }}>
                  Metas / Ações / Produtos (MAP){' '}
                  <span style={{ fontSize: 11, color: 'var(--ink3)', fontWeight: 400 }}>
                    Edite os valores e salve para a emenda recalcular
                  </span>
                </h3>

                <div className="tree" style={{ background: 'var(--surface2)', borderRadius: 6, padding: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 8, borderBottom: '1px solid var(--line)' }}>
                    <div>
                      <b>Emenda Total</b>
                      <small style={{ display: 'block', color: 'var(--ink3)', fontSize: 10 }}>
                        somente leitura: soma das ações
                      </small>
                    </div>
                    <span className="v num" style={{ fontSize: 16, color: 'var(--accent)' }}>
                      <b>{brl(totalDraft)}</b>
                    </span>
                  </div>

                  {acoesDraft.length === 0 && (
                    <div style={{ padding: '10px 0', fontSize: 12, color: 'var(--ink3)' }}>
                      Nenhum item no plano de trabalho.
                    </div>
                  )}

                  {acoesDraft.map((a, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 0 8px 16px',
                        borderBottom: '1px solid var(--line)',
                        fontSize: 13,
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        Ação {i + 1} · <b>{a.nome}</b>
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input
                          type="number"
                          min={0}
                          className="inline-edit num in sm"
                          style={{ width: 95, textAlign: 'right' }}
                          value={a.valor}
                          onChange={(e) => alterarAcao(i, 'valor', Number(e.target.value) || 0)}
                        />
                        <select
                          className="sel sm"
                          value={a.status}
                          onChange={(e) => alterarAcao(i, 'status', e.target.value as StatusExecucao)}
                        >
                          <option value="indicado">indicado</option>
                          <option value="empenhado">empenhado</option>
                          <option value="pago">pago</option>
                        </select>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button
                    className="btn pri"
                    onClick={salvarAcoes}
                    disabled={updateMutation.isPending}
                  >
                    {updateMutation.isPending ? 'Salvando...' : 'Salvar plano de trabalho'}
                  </button>
                  <button
                    className="btn"
                    style={{ color: 'var(--crit)', marginLeft: 'auto' }}
                    onClick={excluir}
                    disabled={deleteMutation.isPending}
                  >
                    Excluir emenda
                  </button>
                </div>

                {/*
                  Fica visível: sem isto, os três estágios se leem como parcelas
                  a somar, e o total da carteira pareceria errado.
                */}
                <div className="note good" style={{ marginTop: 14 }}>
                  <b>Estágios cumulativos:</b> um valor pago já conta como empenhado e indicado.{' '}
                  <Info titulo="Como os totais da carteira são somados">
                    Os totais da carteira são consolidados no servidor, sem dupla contagem.
                  </Info>
                </div>
              </>
            )}

            {/* --- Regime pendente: decisão explícita sobre o MAP proposto --- */}
            {selecionada.regime === 'pendente' && (
              <>
                <dl className="dl" style={{ marginBottom: 14 }}>
                  <dt>Já lançado (regime direto)</dt>
                  <dd className="num">{brl(selecionada.direto?.pago || 0)} pagos</dd>
                  <dt>MAP proposto</dt>
                  <dd>
                    {selecionada.map_proposto?.length
                      ? selecionada.map_proposto
                          .map((a) => `${a.nome} (${brl(a.valor)})`)
                          .join(' · ')
                      : 'Nenhum plano de trabalho anexado.'}
                  </dd>
                </dl>

                <div className="note warn" style={{ marginTop: 14 }}>
                  <b>Transição de regime exige decisão explícita:</b> esta emenda tem{' '}
                  {brl(selecionada.direto?.pago || 0)} lançados diretamente e recebeu um plano de
                  trabalho com {selecionada.map_proposto?.length || 0} ação(ões).{' '}
                  <Info titulo="Por que a escolha é manual">
                    O sistema não escolhe sozinho o que fazer com valores já lançados.
                  </Info>
                </div>

                <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                  <button
                    className="btn pri"
                    disabled={regimeMutation.isPending || !selecionada.map_proposto?.length}
                    onClick={() => regimeMutation.mutate({ id: selecionada.id, decisao: 'migrar' })}
                  >
                    Migrar {brl(selecionada.map_proposto?.reduce((a, x) => a + x.valor, 0) || 0)} para o MAP
                  </button>
                  <button
                    className="btn"
                    disabled={regimeMutation.isPending}
                    onClick={() => regimeMutation.mutate({ id: selecionada.id, decisao: 'descartar' })}
                  >
                    Descartar MAP
                  </button>
                </div>
              </>
            )}

            {/* --- Regime direto: valores lançados pela equipe --- */}
            {selecionada.regime === 'direto' && (
              <>
                <div className="grid g3" style={{ marginTop: 14 }}>
                  {(
                    [
                      ['indicado', 'Indicado'],
                      ['empenhado', 'Empenhado'],
                      ['pago', 'Pago'],
                    ] as Array<[keyof ExecucaoDireta, string]>
                  ).map(([campo, rotulo]) => (
                    <div className="field" key={campo}>
                      <label>{rotulo}</label>
                      <input
                        type="number"
                        min={0}
                        className="in num"
                        value={diretoDraft[campo]}
                        onChange={(e) =>
                          setDiretoDraft({ ...diretoDraft, [campo]: Number(e.target.value) || 0 })
                        }
                      />
                    </div>
                  ))}
                </div>

                <p style={{ fontSize: 12, color: 'var(--ink3)', margin: '10px 0 0' }}>
                  Sem MAP: valores lançados diretamente e editáveis pela equipe.
                </p>

                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button className="btn pri" onClick={salvarDireto} disabled={updateMutation.isPending}>
                    {updateMutation.isPending ? 'Salvando...' : 'Salvar valores'}
                  </button>
                  <button
                    className="btn"
                    style={{ color: 'var(--crit)', marginLeft: 'auto' }}
                    onClick={excluir}
                    disabled={deleteMutation.isPending}
                  >
                    Excluir emenda
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal: Nova emenda */}
      {modalNova && (
        <NovaEmendaModal
          bairros={bairros.map((b) => b.n)}
          exercicio={exercicio}
          salvando={createMutation.isPending}
          onCancelar={() => setModalNova(false)}
          onSalvar={(dados) => createMutation.mutate(dados)}
        />
      )}
    </div>
  );
}

function NovaEmendaModal({
  bairros,
  exercicio,
  salvando,
  onCancelar,
  onSalvar,
}: {
  bairros: string[];
  exercicio: number;
  salvando: boolean;
  onCancelar: () => void;
  onSalvar: (dados: Partial<Emenda>) => void;
}) {
  const [codigo, setCodigo] = useState(`EM-${exercicio}-`);
  const [titulo, setTitulo] = useState('');
  const [bairro, setBairro] = useState('');
  const [beneficiario, setBeneficiario] = useState('');
  const [regime, setRegime] = useState<'map' | 'direto'>('direto');
  const [indicado, setIndicado] = useState(0);

  const submeter = (e: React.FormEvent) => {
    e.preventDefault();
    if (!codigo.trim() || !titulo.trim()) {
      toast('Código e objeto da emenda são obrigatórios.');
      return;
    }
    onSalvar({
      codigo: codigo.trim(),
      titulo: titulo.trim(),
      bairro: bairro || undefined,
      beneficiario: beneficiario.trim() || undefined,
      exercicio,
      regime,
      // No regime direto o valor indicado já entra lançado; no MAP os itens
      // do plano de trabalho é que definem os valores.
      ...(regime === 'direto'
        ? { direto: { indicado, empenhado: 0, pago: 0 } }
        : { acoes: [] }),
    });
  };

  return (
    <form onSubmit={submeter}>
      <Drawer
        aberto
        titulo={`Nova indicação de emenda · ${exercicio}`}
        aoFechar={onCancelar}
        rodape={
          <>
            <button type="button" className="btn" onClick={onCancelar}>
              Cancelar
            </button>
            <button type="submit" className="btn pri" disabled={salvando}>
              {salvando ? 'Salvando...' : 'Indicar emenda'}
            </button>
          </>
        }
      >
        <div className="grid g2">
          <div className="field">
            <label>Código *</label>
            <input
              className="in mono"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              placeholder={`EM-${exercicio}-001`}
              required
            />
          </div>
          <div className="field">
            {/* O ícone fica fora do `label`: dentro, ele herdaria o rótulo do campo. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <label htmlFor="em-regime">Regime</label>
              <Info titulo="O que muda entre direto e MAP">
                No regime direto, a equipe lança os valores aqui. No MAP, a emenda é criada sem
                itens: as ações do plano de trabalho entram no painel de detalhe, e o total passa a
                derivar delas.
              </Info>
            </div>
            <select
              id="em-regime"
              className="in"
              value={regime}
              onChange={(e) => setRegime(e.target.value as any)}
            >
              <option value="direto">Direto · valores lançados pela equipe</option>
              <option value="map">MAP · valores derivados do plano de trabalho</option>
            </select>
          </div>
        </div>

        <div className="field">
          <label>Objeto da emenda *</label>
          <input
            className="in"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="Ex: Reforma de praça e iluminação LED"
            required
          />
        </div>

        <div className="grid g2">
          <div className="field">
            <label>Território</label>
            <select className="in" value={bairro} onChange={(e) => setBairro(e.target.value)}>
              <option value="">Não informado</option>
              {bairros.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Beneficiário</label>
            <input
              className="in"
              value={beneficiario}
              onChange={(e) => setBeneficiario(e.target.value)}
              placeholder="Entidade ou órgão"
            />
          </div>
        </div>

        {regime === 'direto' && (
          <div className="field">
            <label>Valor indicado</label>
            <input
              type="number"
              min={0}
              className="in num"
              value={indicado}
              onChange={(e) => setIndicado(Number(e.target.value) || 0)}
            />
          </div>
        )}

      </Drawer>
    </form>
  );
}
